// src/components/CountyContext.js
import React, {
  createContext,
  useEffect,
  useState,
  useMemo,
  useContext,
} from "react";
import { useStaticQuery, graphql } from "gatsby";

export const CountyContext = createContext();

export const CountyProvider = ({ children }) => {
  const [selectedCounty, setSelectedCounty] = useState("");
  const [selectedCity, setSelectedCity] = useState("");

  // Fetch county/city data
  const data = useStaticQuery(graphql`
    query {
      allCitiesAndCountiesCsv {
        nodes {
          City
          County
        }
      }
    }
  `);

  // Build map: County -> Cities
  const Counties_City_Map = useMemo(() => {
    const map = {};
    data.allCitiesAndCountiesCsv.nodes.forEach((node) => {
      if (node.County?.trim()) {
        if (!map[node.County]) map[node.County] = [];
        if (node.City?.trim()) map[node.County].push(node.City);
      }
    });
    return map;
  }, [data]);

  // Build map: City -> Counties
  const Cities_County_Map = useMemo(() => {
    const map = {};
    data.allCitiesAndCountiesCsv.nodes.forEach((node) => {
      if (node.City?.trim() && node.County?.trim()) {
        if (!map[node.City]) map[node.City] = [];
        map[node.City].push(node.County);
      }
    });
    return map;
  }, [data]);

  // Helper: Update URL params (currently unused; uncomment if needed)
  // const syncParamsToUrl = (county, city) => {
  //   if (typeof window === "undefined") return;
  //   const params = new URLSearchParams(window.location.search);

  //   if (county) params.set("county", county);
  //   else params.delete("county");

  //   if (city) params.set("city", city);
  //   else params.delete("city");

  //   const newUrl =
  //     window.location.pathname +
  //     (params.toString() ? `?${params.toString()}` : "");
  //   window.history.replaceState({}, "", newUrl);
  // };

  // On mount: initialize from URL > localStorage > geolocation
  useEffect(() => {
    if (typeof window === "undefined") return;
    const urlParams = new URLSearchParams(window.location.search);

    const countyFromUrl = urlParams.get("county");
    const cityFromUrl = urlParams.get("city");

    if (countyFromUrl) {
      setSelectedCounty(countyFromUrl);
      localStorage.setItem("selectedCounty", countyFromUrl);
    }
    if (cityFromUrl) {
      setSelectedCity(cityFromUrl);
      localStorage.setItem("selectedCity", cityFromUrl);
    }

    if (!countyFromUrl && localStorage.getItem("selectedCounty")) {
      setSelectedCounty(localStorage.getItem("selectedCounty"));
    }
    if (!cityFromUrl && localStorage.getItem("selectedCity")) {
      setSelectedCity(localStorage.getItem("selectedCity"));
    }

    if (
      !countyFromUrl &&
      !cityFromUrl &&
      !localStorage.getItem("selectedCounty") &&
      !localStorage.getItem("selectedCity") &&
      "geolocation" in navigator
    ) {
      navigator.geolocation.getCurrentPosition((position) => {
        reverseGeocode(position.coords.latitude, position.coords.longitude);
      });
    }

    async function reverseGeocode(latitude, longitude) {
      try {
        const response = await fetch(
          `https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer/reverseGeocode?f=pjson&location=${longitude},${latitude}`
        );
        const data = await response.json();
        if (response.ok && data?.address) {
          const city = data.address.City;
          const county = data.address.Subregion?.split(" ")[0] || "";
          setSelectedCounty(county);
          setSelectedCity(city);
          localStorage.setItem("selectedCounty", county);
          localStorage.setItem("yourCounty", county);
          localStorage.setItem("selectedCity", city);
          localStorage.setItem("yourCity", city);
        }
      } catch (err) {
        // console.error("Geolocation failed:", err);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep city valid for selected county
  useEffect(() => {
    if (
      selectedCounty &&
      selectedCity &&
      !((Counties_City_Map[selectedCounty] || []).includes(selectedCity))
    ) {
      setSelectedCity("");
      localStorage.removeItem("selectedCity");
    }
  }, [selectedCounty, selectedCity, Counties_City_Map]);

  // Keep county valid for selected city
  useEffect(() => {
    if (
      selectedCity &&
      selectedCounty &&
      !((Cities_County_Map[selectedCity] || []).includes(selectedCounty))
    ) {
      setSelectedCounty("");
      localStorage.removeItem("selectedCounty");
    }
  }, [selectedCity, selectedCounty, Cities_County_Map]);

  // Selection handlers
  const handleSelectCounty = (county) => {
    if (county === "yourCounty") {
      const stored = localStorage.getItem("yourCounty");
      if (stored) {
        setSelectedCounty(stored);
        localStorage.setItem("selectedCounty", stored);
      }
    } else {
      setSelectedCounty(county);
      localStorage.setItem("selectedCounty", county);
    }
  };

  const handleSelectCity = (city) => {
    if (city === "yourCity") {
      const stored = localStorage.getItem("yourCity");
      if (stored) {
        setSelectedCity(stored);
        localStorage.setItem("selectedCity", stored);
      }
    } else {
      setSelectedCity(city);
      localStorage.setItem("selectedCity", city);
    }
  };

  return (
    <CountyContext.Provider
      value={{
        selectedCounty,
        handleSelectCounty,
        selectedCity,
        handleSelectCity,
        Counties_City_Map,
        Cities_County_Map,
      }}
    >
      {children}
    </CountyContext.Provider>
  );
};

// Hook to consume county data
export const useCounty = () => useContext(CountyContext);
